import type { CardTag, SpacedRepetitionCard } from './types';

export function isCardDeleted(
  card: Pick<SpacedRepetitionCard, 'deletedTimeISO' | 'actions'>,
): boolean {
  if (card.deletedTimeISO) {
    return true;
  }
  for (const action of card.actions) {
    if (action.delete) {
      return true;
    }
  }
  return false;
}

/**
 * A card's review history — `[]` when the row carries none. Every reader of
 * `studyState` on a card from the store goes through here. The type says
 * `studyState` is required, but a row written past the store-ingress repairs
 * arrives without it, and every unguarded read threw on `.length` (Sentry
 * FLASHKA-APP-JV: one such card took down the deck screen's to-do count).
 * A missing history reads as an EMPTY one, so the card is new — the same rule
 * as `sync-client/deck-summary/facts.ts`.
 */
export function studyStatesOf(
  card: Pick<SpacedRepetitionCard, 'studyState'>,
): SpacedRepetitionCard['studyState'] {
  return Array.isArray(card.studyState) ? card.studyState : [];
}

export function updateTagsAfterEditing(tags: CardTag[]): CardTag[] {
  return tags.filter((tag) => tag.type !== 'leech');
}

/** Structural, so both the SR card and the sync card shapes fit. */
type ProgressFields = {
  actions: ReadonlyArray<{
    good?: { ISO: string };
    bad?: { ISO: string };
    delete?: unknown;
  }>;
  studyState: ReadonlyArray<{ ISO: string }>;
  tags?: ReadonlyArray<{ type: string; timeAddedISO: string }>;
  joinedDeckAtISO?: string;
};

/**
 * The card back to "new" for its owner: no study history, only the `delete`
 * actions (`isCardDeleted` reads them), no leech tag. `studyState` is `[]`,
 * never `undefined` — `migrateCardFromLegacyToAnki` rebuilds a missing one
 * from `actions`.
 */
export function resetCardProgress<T extends ProgressFields>(card: T): T {
  const out = {
    ...card,
    studyState: [],
    actions: (card.actions ?? []).filter((action) => action.delete),
  };
  if (card.tags) out.tags = card.tags.filter((tag) => tag.type !== 'leech');
  return out as T;
}

/** Canonical `toISOString()` shape — safe to compare as strings. */
const CANONICAL_ISO = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/;

/**
 * `iso` is before the floor. Canonical UTC strings compare lexically; any
 * other shape (no millis, an offset — imported/legacy rows) by instant. An
 * unreadable timestamp counts as before: the reset clears it.
 */
function isBeforeFloor(iso: string, floorISO: string, floorMs: number): boolean {
  if (CANONICAL_ISO.test(iso) && CANONICAL_ISO.test(floorISO)) return iso < floorISO;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) || ms < floorMs;
}

/**
 * Apply a deck's progress-reset floor. A card with ANY study entry older than
 * the floor is reset whole — its newest entry's scheduling is derived from the
 * older ones, so dropping only the old entries would keep their effect. A card
 * studied only since the floor is returned as the same reference, and so is a
 * card that JOINED the deck after the floor (`joinedDeckAtISO`, stamped when a
 * card is moved or merged into a reset deck): its history is from before it
 * was in this deck, and the reset never applied to it.
 */
export function applyProgressFloor<T extends ProgressFields>(
  card: T,
  floorISO: string | null | undefined,
): T {
  if (!floorISO) return card;
  const floorMs = Date.parse(floorISO);
  if (card.joinedDeckAtISO && !isBeforeFloor(card.joinedDeckAtISO, floorISO, floorMs)) {
    return card;
  }
  const before = (iso: string) => isBeforeFloor(iso, floorISO, floorMs);
  let stale = false;
  for (const state of card.studyState ?? []) {
    if (before(state.ISO)) {
      stale = true;
      break;
    }
  }
  if (!stale) {
    for (const action of card.actions ?? []) {
      const iso = action.good?.ISO ?? action.bad?.ISO;
      if (iso !== undefined && before(iso)) {
        stale = true;
        break;
      }
    }
  }
  if (!stale && card.tags) {
    for (const tag of card.tags) {
      if (tag.type === 'leech' && before(tag.timeAddedISO)) {
        stale = true;
        break;
      }
    }
  }
  return stale ? resetCardProgress(card) : card;
}
